import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Api } from "../../network/api";
import theme from "../../theme/theme";
import {
  setUsers,
  setUsersLoading,
  setUsersError,
  setSelectedUser,
  clearUsersError,
} from "../../redux/slice/globalSlice";

const UsersManagement = () => {
  const dispatch = useDispatch();
  const { users, usersLoading, usersError, selectedUser } = useSelector(
    (state) => state.global
  );
  const [showModal, setShowModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loadingActions, setLoadingActions] = useState({});

  const fetchUsers = async () => {
    try {
      dispatch(setUsersLoading(true));
      dispatch(clearUsersError());

      const response = await Api.getUsers();
      dispatch(setUsers(response?.data));
      dispatch(setUsersLoading(false));
    } catch (error) {
      console.error("Error fetching users:", error);
      dispatch(setUsersError(error.message || "Failed to fetch users"));
      dispatch(setUsersLoading(false));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBlockUser = async (userId) => {
    try {
      setLoadingActions((prev) => ({ ...prev, [userId]: true }));
      const response = await Api.blockedUser(userId);
      console.log("Block user response:", response);
      const updatedUsers = users.map((user) =>
        user.id === userId ? { ...user, is_blocked: true } : user
      );
      dispatch(setUsers(updatedUsers));
      setOpenDropdown(null);
    } catch (error) {
      console.error("Error blocking user:", error);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      setLoadingActions((prev) => ({ ...prev, [userId]: true }));
      const response = await Api.unblockedUser(userId);
      console.log("Unblock user response:", response);
      const updatedUsers = users.map((user) =>
        user.id === userId ? { ...user, is_blocked: false } : user
      );
      dispatch(setUsers(updatedUsers));

      setOpenDropdown(null);
    } catch (error) {
      const errorData = error.response?.data?.detail;
      console.log("Error data :", error.response?.data.detail);
      console.log(
        "Error unblocking user:",
        error.response?.data?.detail.errorMessage
      );
      console.log("Error message : ", error.response?.data?.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleStatus = (userId) => {
    const updatedUsers = users.map((user) =>
      user.id === userId ? { ...user, isActive: !user.isActive } : user
    );
    dispatch(setUsers(updatedUsers));
  };

  const toggleDropdown = (userId) => {
    setOpenDropdown(openDropdown === userId ? null : userId);
  };

  const openEssaysModal = (user) => {
    dispatch(setSelectedUser(user));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    dispatch(setSelectedUser(null));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-center w-full">
              <h1 className="text-2xl font-bold text-gray-900">
                Users Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your application users and their permissions.
              </p>
            </div>
          </div>
        </div>

        {usersLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading users...</span>
          </div>
        )}
        {!usersLoading && !usersError && (
          <div className="overflow-x-auto">
            <div className="h-[700px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      User
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Submissions
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Last Submission
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Essays
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Actions
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Subscription
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-gray-50 ${
                      user.is_blocked ? "bg-red-50" : ""
                    } h-20 transition-all duration-200 ease-in-out`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.submission_count}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(user.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openEssaysModal(user)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Essays ({user.submission_count})
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_blocked
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {user.is_blocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="dropdown-container relative">
                        <button
                          onClick={() => toggleDropdown(user.id)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out min-w-[120px] justify-between"
                        >
                          <span>
                            {user.is_admin
                              ? "Admin"
                              : user.is_blocked
                              ? "Blocked"
                              : "User"}
                          </span>
                          <svg
                            className={`ml-2 h-4 w-4 transition-transform duration-200 ${
                              openDropdown === user.id ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        {openDropdown === user.id && (
                          <div className="absolute right-0 mt-2 w-44 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1" role="menu">
                              {user.is_blocked ? (
                                <button
                                  onClick={() => handleUnblockUser(user.id)}
                                  disabled={loadingActions[user.id]}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                  role="menuitem"
                                >
                                  {loadingActions[user.id]
                                    ? "Unblocking..."
                                    : "Unblock User"}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBlockUser(user.id)}
                                  disabled={loadingActions[user.id]}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                  role="menuitem"
                                >
                                  {loadingActions[user.id] ? (
                                    <div className="w-4 h-4 mr-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <svg
                                      className="w-4 h-4 mr-3 text-red-600"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                  {loadingActions[user.id]
                                    ? "Blocking..."
                                    : "Block User"}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center space-y-2">
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ease-in-out ${
                            user.isActive
                              ? "bg-red-100 text-red-700 hover:bg-red-200"
                              : "bg-green-100 text-green-700 hover:bg-green-200"
                          }`}
                        >
                          {user.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Essays by {selectedUser.name}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                {selectedUser.essays.map((essay, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex flex-row justify-between items-center w-full">
                      <div className="flex flex-col items-start">
                        <p className="text-sm font-medium text-gray-900">
                          {essay.subject_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Uploaded: {formatDate(essay.upload_time)}
                        </p>
                      </div>
                      {/* <a
                        href={`/${essay.essay_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-medium hover:underline"
                      >
                        View Essay
                      </a> */}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
